import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Checkbox from '@zenbusiness/zui/Checkbox';
import Chip from '@zenbusiness/zui/Chip';
import FormControl from '@zenbusiness/zui/FormControl';
import FormHelperText from '@zenbusiness/zui/FormHelperText';
import ListItemText from '@zenbusiness/zui/ListItemText';
import MenuItem from '@zenbusiness/zui/MenuItem';
import Select from '@zenbusiness/zui/Select';

import FieldHeading from './FieldHeading';
import { getFieldOptions } from './FieldOptionRegistry';

/**
 * Dropdown multi select field
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {Array} props.value - Array of selected values [{selectedValue, value}]
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 */
function DropdownMultiSelectField({ def, value = [], onChange, disabled = false, error }) {
  const options = getFieldOptions(def.options) || [];

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    const currentValues = Array.isArray(value) ? value : [];

    // Check if selection is within limits
    if (def.maxSelected && selectedValues.length > def.maxSelected) {
      return; // Don't allow selection beyond max
    }

    // Map selected IDs to value objects
    const newValues = selectedValues.map((selectedValue) => {
      // Try to find existing value to preserve it
      const existingValue = currentValues.find((v) => v.value === selectedValue);
      if (existingValue) {
        return existingValue;
      }

      // Otherwise, create new value object
      const option = options.find((opt) => opt.value === selectedValue);
      return {
        value: selectedValue,
        label: option?.label
      };
    });

    onChange(newValues);
  };

  const handleDelete = (valueToDelete) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.filter((v) => v.value !== valueToDelete);
    onChange(newValues);
  };

  const fieldId = `dropdown-multi-select-${def.id}`;
  const hasError = Boolean(error);
  const currentValues = Array.isArray(value) ? value : [];
  const selectedValues = currentValues.map((v) => v.value);
  const isAtLimit = def.maxSelected && currentValues.length >= def.maxSelected;

  // Render selected values as chips
  const renderValue = (selection) => {
    if (selection.length === 0) {
      return <em>{def.placeholder || 'Select options'}</em>;
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {selection.map((selectedValue) => {
          const selectedOption = currentValues.find((v) => v.value === selectedValue);
          return (
            <Chip
              key={selectedValue}
              label={selectedOption?.label || selectedValue}
              size="small"
              onDelete={disabled ? undefined : () => handleDelete(selectedValue)}
              onMouseDown={(event) => {
                event.stopPropagation(); // Prevent dropdown from opening when clicking chip
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
      variant="outlined"
    >
      <FieldHeading
        description={def.description}
        label={def.label}
        labelId={`${fieldId}-label`}
        required={def.required}
      />

      <Box mt={2}>
        <Select
          disabled={disabled}
          id={fieldId}
          label={
            def.label && def.maxSelected
              ? `${def.label} (Select up to ${def.maxSelected})`
              : def.label
          }
          labelId={`${fieldId}-label`}
          multiple
          renderValue={renderValue}
          value={selectedValues}
          onChange={handleChange}
        >
          {options?.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const isDisabled = disabled || (isAtLimit && !isSelected);

            return (
              <MenuItem key={option.value} disabled={isDisabled} value={option.value}>
                <Checkbox checked={isSelected} disabled={isDisabled} />
                <ListItemText primary={option.label} />
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      {(error || def.helpText) && <FormHelperText>{error || def.helpText}</FormHelperText>}
    </FormControl>
  );
}

DropdownMultiSelectField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['dropdown_multi_select']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    maxSelected: PropTypes.number,
    options: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
  }).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default DropdownMultiSelectField;
